import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Stethoscope } from "lucide-react";
import { CONTACT, WHATSAPP_BASE } from "@/data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/surgery-guide", label: "Surgery Guide" },
  { to: "/faqs", label: "FAQs" },
  { to: "/recovery", label: "Recovery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 w-full bg-white border-b border-brand-line"
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            data-testid="nav-logo"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-primary text-white">
              <Stethoscope className="h-5 w-5" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-[15px] font-extrabold tracking-tight text-brand-ink">
                Advanced ENT Surgery Centre
              </span>
              <span className="block text-[11px] font-medium uppercase tracking-wider text-brand-action">
                Dr. Sanjay Teza · Varanasi
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-soft text-brand-primary"
                      : "text-brand-slate hover:bg-brand-soft/60 hover:text-brand-primary"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="nav-call-link"
              className="flex items-center gap-2 text-sm font-semibold text-brand-ink transition-colors hover:text-brand-primary"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>
            <a
              href={`${WHATSAPP_BASE}?text=${encodeURIComponent(
                "Hello Dr. Sanjay Teza, I would like to book a surgical consultation.",
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-cta"
              className="rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-action"
            >
              Book Consultation
            </a>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center rounded-md p-2 text-brand-ink lg:hidden"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-brand-line bg-white lg:hidden" data-testid="nav-mobile-menu">
          <nav className="mx-auto max-w-7xl space-y-1 px-4 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `block rounded-md px-3.5 py-3 text-base font-medium ${
                    isActive
                      ? "bg-brand-soft text-brand-primary"
                      : "text-brand-slate"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={`${WHATSAPP_BASE}?text=${encodeURIComponent(
                "Hello Dr. Sanjay Teza, I would like to book a surgical consultation.",
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-book-cta"
              className="mt-2 block rounded-full bg-brand-primary px-5 py-3 text-center text-base font-semibold text-white"
            >
              Book Consultation on WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
